import { ConsentChoices, ConsentRecord, ConsentSource } from "../types";
import { readConsentConfig } from "./consentConfig";

/**
 * The `dapple_consent` cookie: the visitor's recorded choice, shared with
 * `visitor.js` on the clinic's website (same name, same format, same domain
 * rule as `dapple_visitor_id`). See CONSENT.md in the backend repo.
 *
 * Value: URL-encoded JSON
 * `{"v":1,"a":true,"m":false,"p":"<policy>","t":<ms>,"s":"banner","o":"<tenant>"}`.
 * `o` is the tenant the choice was made for: a record for another tenant is
 * ignored, so a choice made for one clinic can never stand in for another's
 * on a shared domain.
 * First-party, `path=/`, 12 months, `SameSite=Lax`, `Secure` on https, on the
 * registrable domain the backend injects (`cookieDomain`), or host-only when it
 * injects none (shared Dapple domains). Mirrored to localStorage for browsers
 * that block the cookie.
 *
 * Dependency-free so the boot path (Datadog setup) can read it before React.
 */
export const CONSENT_COOKIE_NAME = "dapple_consent";

/** Cross-domain hand-off `visitor.js` adds to links into the portal: `?dapple_consent=10`. */
export const CONSENT_URL_PARAM = "dapple_consent";

const ONE_YEAR_SECONDS = 365 * 24 * 60 * 60;

const SOURCES: readonly ConsentSource[] = [
  "banner",
  "gcm",
  "tcf",
  "cookiebot",
  "onetrust",
  "api",
  "gpc",
  "link",
];

/** Anything longer than this is not a cookie we wrote. */
const MAX_RAW_LENGTH = 1024;

/**
 * Decodes a raw cookie or storage value. Returns null for anything missing,
 * unparseable or the wrong shape; the caller treats null as "not decided".
 */
export const parseConsentRecord = (
  raw: string | null | undefined,
): ConsentRecord | null => {
  if (!raw || raw.length > MAX_RAW_LENGTH) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(decodeURIComponent(raw));
  } catch {
    try {
      // localStorage holds the JSON unencoded; a cookie set by hand might too.
      parsed = JSON.parse(raw);
    } catch {
      return null;
    }
  }

  if (typeof parsed !== "object" || parsed === null) return null;
  const record = parsed as Record<string, unknown>;

  if (record.v !== 1) return null;
  if (typeof record.a !== "boolean" || typeof record.m !== "boolean") {
    return null;
  }

  const source = SOURCES.find(s => s === record.s) ?? "api";

  return {
    v: 1,
    a: record.a,
    m: record.m,
    p: typeof record.p === "string" ? record.p : "",
    t: typeof record.t === "number" && Number.isFinite(record.t) ? record.t : 0,
    s: source,
    ...(typeof record.o === "string" && record.o ? { o: record.o } : {}),
  };
};

/** Encodes a record for the cookie. */
export const serialiseConsentRecord = (record: ConsentRecord): string =>
  encodeURIComponent(JSON.stringify(record));

/** Builds a record for a decision made now. */
export const buildConsentRecord = (
  choices: ConsentChoices,
  policyVersion: string,
  source: ConsentSource,
  now: number = Date.now(),
  tenant?: string,
): ConsentRecord => ({
  v: 1,
  a: choices.analytics,
  m: choices.marketing,
  p: policyVersion,
  t: now,
  s: source,
  ...(tenant ? { o: tenant } : {}),
});

/**
 * A record applies only to the policy it was given under. A changed
 * `trackingConsentPolicyVersion` asks every visitor again.
 */
export const recordAppliesToPolicy = (
  record: ConsentRecord | null,
  policyVersion: string,
): record is ConsentRecord => !!record && record.p === policyVersion;

/**
 * A record applies only to the tenant it was made for: on a shared domain the
 * cookie could otherwise carry one clinic's consent into another's portal.
 *
 * - No `o` at all: never applies. Every current writer (this portal and
 *   `visitor.js`) stamps it, so an unstamped record is from an older build,
 *   possibly written on a parent domain shared by many clinics.
 * - `o` for another tenant: never applies.
 * - Before the current tenant is known (no injected tenant, portal config not
 *   loaded yet) a stamped record is provisionally accepted, and the store
 *   re-checks it the moment the tenant arrives (`setConsentTenant`).
 */
export const recordAppliesToTenant = (
  record: ConsentRecord | null,
  tenant: string | undefined,
): boolean => !!record?.o && (!tenant || record.o === tenant);

const readCookie = (name: string): string | null => {
  try {
    const cookie = globalThis.document?.cookie;
    if (typeof cookie !== "string" || !cookie) return null;

    const prefix = `${name}=`;
    const entry = cookie
      .split(";")
      .map(part => part.trim())
      .find(part => part.startsWith(prefix));

    return entry ? entry.slice(prefix.length) : null;
  } catch {
    return null;
  }
};

const localStore = (): Storage | null => {
  try {
    return globalThis.window?.localStorage ?? null;
  } catch {
    return null;
  }
};

/**
 * The stored record: the cookie first (shared with the clinic's website on the
 * same registrable domain), then the localStorage mirror. Never throws.
 */
export const readConsentRecord = (): ConsentRecord | null => {
  const fromCookie = parseConsentRecord(readCookie(CONSENT_COOKIE_NAME));
  if (fromCookie) return fromCookie;

  try {
    return parseConsentRecord(localStore()?.getItem(CONSENT_COOKIE_NAME));
  } catch {
    return null;
  }
};

const isHttps = (): boolean => {
  try {
    return globalThis.location?.protocol === "https:";
  } catch {
    return false;
  }
};

/**
 * The attribute string every cookie here is written with. Exported for tests:
 * a browser never reads attributes back, so this is where they are asserted.
 */
export const buildCookieAttributes = (
  maxAgeSeconds: number,
  domain: string | null,
  secure: boolean,
): string =>
  [
    "path=/",
    `max-age=${maxAgeSeconds}`,
    "SameSite=Lax",
    ...(secure ? ["Secure"] : []),
    ...(domain ? [`domain=.${domain}`] : []),
  ].join("; ");

/** The consent cookie lasts 12 months, per the contract. */
export const CONSENT_COOKIE_MAX_AGE_SECONDS = ONE_YEAR_SECONDS;

const cookieAttributes = (maxAgeSeconds: number, domain: string | null) =>
  buildCookieAttributes(maxAgeSeconds, domain, isHttps());

/** Whether `hostname` is `domain` or a subdomain of it. */
export const isWithinDomain = (hostname: string, domain: string): boolean => {
  const host = hostname.toLowerCase();
  const target = domain.toLowerCase();
  return host === target || host.endsWith(`.${target}`);
};

/**
 * The cookie `domain` for this page: the `cookieDomain` the backend injected
 * (computed server-side with tldts, exactly as `visitor.js` gets it), when this
 * page's host is within it. Otherwise null, meaning a host-only cookie.
 *
 * Deliberately no client-side guess. On shared Dapple domains
 * (`<clinic>.dapplecrm.com`) the backend injects an empty `cookieDomain`, and
 * a guessed parent domain there would share one clinic's consent and visitor
 * cookies with every other clinic on it.
 */
export const consentCookieDomain = (): string | null => {
  try {
    const hostname = globalThis.location?.hostname ?? "";
    const configured = readConsentConfig().cookieDomain;
    return configured && isWithinDomain(hostname, configured)
      ? configured
      : null;
  } catch {
    return null;
  }
};

/**
 * Writes the record to the cookie (on the registrable domain where possible)
 * and mirrors it to localStorage. Never throws: a blocked cookie still leaves
 * the mirror, and a blocked mirror still leaves the in-memory store.
 */
export const writeConsentRecord = (record: ConsentRecord): void => {
  const value = serialiseConsentRecord(record);

  try {
    const doc = globalThis.document;
    if (doc) {
      doc.cookie = `${CONSENT_COOKIE_NAME}=${value}; ${cookieAttributes(
        ONE_YEAR_SECONDS,
        consentCookieDomain(),
      )}`;
    }
  } catch {
    // Cookies blocked: the mirror below is the fallback the contract names.
  }

  try {
    localStore()?.setItem(CONSENT_COOKIE_NAME, JSON.stringify(record));
  } catch {
    // Storage blocked too: the decision still holds for this page load.
  }
};

/**
 * Expires a cookie on this host and on the registrable domain, since
 * `visitor.js` may have set it on either.
 */
export const deleteCookieEverywhere = (name: string): void => {
  try {
    const doc = globalThis.document;
    if (!doc) return;
    doc.cookie = `${name}=; ${cookieAttributes(0, null)}`;
    const domain = consentCookieDomain();
    if (domain) doc.cookie = `${name}=; ${cookieAttributes(0, domain)}`;
  } catch {
    // Nothing more we can do.
  }
};

/** The analytics-category storage `visitor.js` keeps. Deleted on withdraw. */
export const ANALYTICS_STORAGE_KEYS = [
  "dapple_visitor_id",
  "dapple_session",
  "dapple_utm_params",
] as const;

/** The marketing-category storage the portal keeps. Deleted on withdraw. */
export const MARKETING_SESSION_KEYS = ["dapple.trackingParams"] as const;

/**
 * Prefix of the session markers `useLinkVisitorToPatient` writes once a visitor
 * id is linked to the signed-in patient (`dapple_visitor_linked:<customer>:<visitor>`).
 * They name a visitor id, so they are analytics storage too.
 */
export const VISITOR_LINKED_KEY_PREFIX = "dapple_visitor_linked";

/**
 * Deletes `dapple_visitor_id`, `dapple_session` and `dapple_utm_params`
 * everywhere they live, and the portal's visitor-linked session markers.
 */
export const clearAnalyticsStorage = (): void => {
  for (const key of ANALYTICS_STORAGE_KEYS) {
    // Only when present, so nothing is written for a visitor with nothing
    // stored.
    if (readCookie(key) !== null) deleteCookieEverywhere(key);
    try {
      localStore()?.removeItem(key);
    } catch {
      // Storage blocked: nothing was stored there either.
    }
  }
  try {
    const session = globalThis.window?.sessionStorage;
    if (session) {
      const linked: string[] = [];
      for (let i = 0; i < session.length; i++) {
        const key = session.key(i);
        if (key?.startsWith(VISITOR_LINKED_KEY_PREFIX)) linked.push(key);
      }
      for (const key of linked) session.removeItem(key);
    }
  } catch {
    // Storage blocked: nothing was stored there either.
  }
};

/** Deletes the captured campaign parameters (`dapple.trackingParams`). */
export const clearMarketingStorage = (): void => {
  for (const key of MARKETING_SESSION_KEYS) {
    try {
      globalThis.window?.sessionStorage?.removeItem(key);
    } catch {
      // Storage blocked: nothing was stored there either.
    }
  }
};

/**
 * Reads the cross-domain hand-off `visitor.js` puts on links into the portal:
 * `dapple_consent=<a><m>` with each digit 1 or 0 (`10` is analytics only).
 */
export const parseCrossDomainConsent = (
  search: string | null | undefined,
): ConsentChoices | null => {
  if (!search) return null;
  try {
    const value = new URLSearchParams(search).get(CONSENT_URL_PARAM);
    if (!value || !/^[01]{2}$/.test(value)) return null;
    return { analytics: value[0] === "1", marketing: value[1] === "1" };
  } catch {
    return null;
  }
};
